'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { launchpoolBunja } from '@flipster/features/src/shared/launchpool/state';
import XMarkIcon from '@flipster/react/icon/generated/XMarkIcon';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';

const NowUseYourSparksBanner = () => {
  const { isMobile } = useResponsive();
  const { isClosed, onClose } = useSparkBanner();
  const { ongoingSparkLaunchpoolExistsAtom } = useBunja(launchpoolBunja);
  const ongoingSparkLaunchpoolExists = useAtomValue(ongoingSparkLaunchpoolExistsAtom);
  if (isClosed || !ongoingSparkLaunchpoolExists) return null;
  return isMobile ? <MobileBanner onClose={onClose} /> : <PcBanner onClose={onClose} />;
};
export default NowUseYourSparksBanner;

function useSparkBanner() {
  const [isClosed, setIsClosed] = useState(true);
  const onClose = useCallback(() => {
    setIsClosed(true);
    addClosedLaunchpoolPageBanner('NowUseYourSparksBanner');
  }, []);
  useEffect(() => {
    const closedLaunchpoolPageBanners = getClosedLaunchpoolPageBanners();
    setIsClosed(closedLaunchpoolPageBanners.includes('NowUseYourSparksBanner'));
  }, []);
  return { isClosed, onClose };
}

function getClosedLaunchpoolPageBanners() {
  return getLocalStorage('flipster.configs.v1.closedLaunchpoolPageBanner') || [];
}

function addClosedLaunchpoolPageBanner(id: string) {
  const closedLaunchpoolPageBanners = getClosedLaunchpoolPageBanners();
  const newClosedLaunchpoolPageBanners = new Set([...closedLaunchpoolPageBanners, id]);
  setLocalStorage('flipster.configs.v1.closedLaunchpoolPageBanner', Array.from(newClosedLaunchpoolPageBanners));
}

interface BannerProps {
  onClose: () => void;
}

const MobileBanner = ({ onClose }: BannerProps) => {
  return (
    <div
      className={flex({
        mb: 20,
        rounded: 8,
        justifyContent: 'space-between',
        bg: 'background.default.level-2',
      })}
    >
      <div className={stack({ py: 12, pl: 16 })}>
        <span className={css({ mr: 8, color: 'content.accent.promotion', textStyle: 'Text/M/Strong' })}>
          NEW in Launchpool
        </span>
        <span className={css({ flexGrow: 1, color: 'content.default.level-4', textStyle: 'Text/M/Regular' })}>
          Now use your Sparks to redeem a share
        </span>
      </div>
      <button className={css({ alignSelf: 'flex-start', m: 4, p: 10 })} onClick={onClose}>
        <XMarkIcon size={16} className={css({ color: 'content.default.level-3' })} />
      </button>
    </div>
  );
};

const PcBanner = ({ onClose }: BannerProps) => {
  return (
    <div
      className={flex({
        mb: 20,
        py: 12,
        pl: 16,
        pr: 14,
        alignItems: 'center',
        rounded: 8,
        bg: 'background.default.level-2',
      })}
    >
      <span className={css({ mr: 8, color: 'content.accent.promotion', textStyle: 'Text/M/Strong' })}>NEW</span>
      <span className={css({ flexGrow: 1, color: 'content.default.level-4', textStyle: 'Text/M/Regular' })}>
        Now use your Sparks to redeem a share of the Launchpool
      </span>
      <button onClick={onClose}>
        <XMarkIcon size={16} className={css({ color: 'content.default.level-3' })} />
      </button>
    </div>
  );
};
